import React, { useEffect, useState } from 'react';
import { Button, Header, Input, Label, Message, Table, Icon, useFrontier, Modal } from 'react-frontier2';
import { SetLoading } from '@arema/components/Classes';
import { bindFormChange, addCommas } from '@arema/components/Util';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { hashPaymentOrder } from '../AdminUtil';
import API from '../API';
import moment from 'moment';
import Validator from '@arema/components/Validator';
import UserLog from './UserLog';

interface ExternalPayment {
	status: string,
	amount: number,
	paid: boolean,
	cancelled: boolean,
	date: number,
	reference: string,
	bank: string,
	card_digits: string,
	error: boolean,
	error_message: string,
}

interface PaymentOrderInfo {
	payment_id: number,
	delivery_cost: number,
	order_id: number
	total: number,
	payment_cost: number,
	pdv_name: string,
	pdv_id: string,
	method_name: string,
	method_id: number,
	reference: string,
	external_id: string,
	date_paid: number,
	payment_link: string,
	date_executed: number,
	date_cancelled: number,
	cancelled: boolean,
	paid: boolean,
	external_payment: ExternalPayment,
	user_creator: number,
	user_paid: number,
	user_cancelled: number,
}

interface ModalProps {
	open: boolean,
	editable?: boolean,
	onClose: () => void,
	paymentId: number
	onPaymentPaid?: (payment_id: number, tickets: number[])=>void,
	onPaymentEdit?: (paymentInfo: { payment_id: number, payment_cost: number, delivery_cost: number, total: number }) => void,
}

var PaymentInfoModal = (props: ModalProps) => {
	var { t } = useTranslation();
	var { Toast } = useFrontier();
	var [loadError, setLoadError] = useState<string>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [editPayment, setEditPayment] = useState<boolean>(false);
	var [form, setForm] = useState<PaymentOrderInfo>(null);
	let payment_id = props.paymentId || null

	useEffect(() => {
		setEditPayment(false);
		if(props.open && !Number.isNaN(parseInt(payment_id.toString()))) {
			if(props.paymentId!==form?.payment_id){
				loadData(payment_id);
			}
		}
	}, [props.open, props.paymentId]);

	useEffect(()=>{
		if(!props.open) setForm(null);
	}, [props.open])

	const loadData = async (payment_id: number) => {
		setForm(null);
		setLoadError(null);
		API.getPaymentInfo(payment_id).then(res => {
			if (res.error) return setLoadError(res.message);
			setForm(res.data);
		}).catch(err => {
			setModalPrompts(['Hubo un error cargando la cuenta (LCL-3)']);
		})
	}

	var submit = (setLoading: SetLoading) => {
		var { valid, prompts } = Validator(form, {
			payment_cost: [
				{ rule: 'float', label: 'Costo' },
				{ rule: 'min', params: [0], prompt: 'La cantidad minima es 0.' },
			],
			delivery_cost: [
				{ rule: 'float', label: 'Costo de envio' },
				{ rule: 'min', params: [0], prompt: 'La cantidad minima es 0.' },
			],
			total: [
				{ rule: 'float', label: 'Total' },
				{ rule: 'min', params: [0], prompt: 'La cantidad minima es 0.' },
			],
		});

		setModalPrompts(prompts);
		if(!valid) return;

		setLoading(true);
		API.updatePayment(payment_id, parseFloat(form.payment_cost.toString()), parseFloat(form.delivery_cost.toString()), parseFloat(form.total.toString())).then(res => {
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se guardaron los datos correctamente');
			if (props.onPaymentEdit) {
				props.onPaymentEdit({ 
					payment_id: parseInt(payment_id.toString()),
					payment_cost: parseFloat(form.payment_cost.toString()),
					delivery_cost: parseFloat(form.delivery_cost.toString()),
					total: parseFloat(form.total.toString())
				});
			}
			setEditPayment(false);
		}).catch(err => {
			Toast.error(`Hubo un error inesperado guardando los datos (LCL-10)`);
		}).finally(() => {
			setLoading(false)
		});
	}

	var sendPaymentPaid = (setLoading: SetLoading)=>{
		setLoading(true);
		API.setPaymentPaid(form.payment_id).then(res=>{
			if(res.error) return Toast.error(res.message);
			if(props.onPaymentPaid) props.onPaymentPaid(form.payment_id, res.data.tickets);
			setForm({
				...form,
				paid: true,
				cancelled: false,
			})
		}).catch(err=>{
			Toast.error(t('common.unexpected_code', { code: 'LCL-PYINFMD-1' }));
		}).finally(()=>{
			setLoading(false);
		})
	}

	var checkPaymentPaid = (setLoading: SetLoading)=>{
		
	}

	var onDataChange = bindFormChange(form, setForm);

	return <Modal open={props.open} onClose={props.onClose} size='tiny'>
		<Modal.Header>Informacion de Pago</Modal.Header>
		<Modal.Content>
			{loadError ? (
				<Header text='Error' iconName='face-frown-open' subtext={loadError} />
			) : form===null ? (
				<Header loading text='Cargando Pago' />
			) : (form ? <>
				{form.external_payment?.paid && !form.paid && (
					<Message type='info' style={{ marginBottom: 15 }} header='Advertencia' centered>
						El procesador tiene registrado este pago como <b>COMPLETADO</b> pero el sistema <b>NO lo tiene registrado así.</b> <br />
						Favor de marcar el pago como <b>COMPLETADO</b>
						<br />
						<Button color='blue' style={{ marginTop: 10, paddingLeft: 20, paddingRight: 20 }} text='Marcar pagado' size='small' onClick={sendPaymentPaid} />
					</Message>
				)}
				<Table details>
					<Table.Head title='Información' titleSize='small' />
					<Table.Body>
						<Table.Row data={['ID', form.payment_id]} />
						<Table.Row data={['Ref', hashPaymentOrder(form.order_id, form.payment_id)]} />
						{!!form.pdv_id &&
							<Table.Row as={Link} to={`/pdv/${form.pdv_id}`} compact data={['PDV', `[${form.pdv_id}] ${form.pdv_name}`]} />
						}
						<Table.Row data={['Estatus', <>
							<div className={`fr label ${form.cancelled ? 'red' : (form.paid ? 'green' : 'blue')}`}>
								{form.cancelled ? 'Cancelado' : (form.paid ? 'Pagado' : 'Pendiente')}
							</div>
						</>]} />

						{form.method_name ?
							<Table.Row data={['Metodo de pago', form.method_name]} /> : null
						}
						<Table.Row data={['Referencia', form.reference || <Icon name='minus' color='grey' />]} />
						{!!form.external_id && (
							<Table.Row>
								<Table.Cell>ID Externo</Table.Cell>
								<Table.Cell>
									<Link className='normal' to={form.payment_link} target="_blank" >
										{form.external_id}
									</Link>
								</Table.Cell>
							</Table.Row>
						)}
						<Table.Divider />
						{form.date_executed ?
							<Table.Row data={['Procesado', moment.unix(form.date_executed).format('DD/MM/YY HH:mm:ss')]} /> : null
						}
						{form.date_paid ?
							<Table.Row data={['Fecha pago', <>
								{moment.unix(form.date_paid).format('DD/MM/YY HH:mm:ss')}
								<div className="meta">({moment().diff(moment.unix(form.date_paid), 'days')} días)</div>
							</>]} /> : null
						}
						{form.date_cancelled ?
							<Table.Row data={['Fecha cancelado', moment.unix(form.date_cancelled).format('DD/MM/YY HH:mm:ss')]} /> : null
						}
					</Table.Body>
				</Table>

				<Table details striped style={{ marginTop: 15 }}>
					<Table.Head title='Cantidades' titleSize='small' titleRight={(
						props.editable && (
						!!editPayment ? (
							<Button color="green" text="Listo" size="tiny" iconName={'check'} onClick={submit} />
						) : (
							<Button color="black" text="Cambiar" size="tiny" iconName={'pen'} onClick={() => { setEditPayment(true) }} />
						)
					))} />
					<Table.Body>
						<Table.Row data={['Comisión bancaria', editPayment ? (
							<Input onChange={onDataChange('payment_cost')} style={{ marginBottom: 0 }} value={form.payment_cost} inputType='number' />
						) : (
							`$${addCommas(form.payment_cost)}`
						)]} />
						<Table.Row data={['Cargo envío', editPayment ? (
							<Input style={{ marginBottom: 0 }} onChange={onDataChange('delivery_cost')} value={form.delivery_cost} inputType='number' />
						) : (
							`$${addCommas(form.delivery_cost)}`
						)]} />
						<Table.Divider />
						<Table.Row data={['Total pagado', editPayment ? (
							<Input style={{ marginBottom: 0 }} onChange={onDataChange('total')} value={form.total} inputType='number' />
						) : (
							`$${addCommas(form.total)}`
						)]} />
					</Table.Body>
				</Table>

				{!!(form.user_paid || form.user_creator || form.user_cancelled) && (
					<UserLog user={form} style={{ maxWidth: 1000 }} titleSize={'small'} />
				)}

				{!!form.external_payment && !form.external_payment.error &&
					<Table details style={{ marginTop: 20 }}>
						<Table.Head title='Información externa' titleSize='small' />
						<Table.Body>
							<Table.Row data={['Procesador', form.method_name]} />
							{!form.external_payment.error && (<>
								<Table.Row data={['Referencia', form.external_payment.reference]} />
								<Table.Row data={['Estado', (
									<Label color={form.external_payment.cancelled ? 'red' : form.external_payment.paid ? 'green' : 'blue'}>
										{form.external_payment.cancelled ? 'Cancelado' : form.external_payment.paid ? 'Pagado' : 'Pendiente' }
									</Label>
								)]} />
								<Table.Row data={['Estatus', form.external_payment.status]} />
								<Table.Row data={['Cantidad', addCommas(form.external_payment.amount)]} />
								<Table.Row data={['Fecha', moment.unix(form.external_payment.date).format('DD/MM/YY HH:mm:ss')]} />
								{!!form.external_payment.bank && (
									<Table.Row data={['Banco', form.external_payment.bank]} />
								)}
								{!!form.external_payment.card_digits && (
									<Table.Row data={['Tarjeta', form.external_payment.card_digits]} />
								)}
							</>)}
						</Table.Body>
					</Table>
				}
				{!!form.external_payment && !!form.external_payment.error && (
					<Message type='error' style={{ marginTop: 15 }}>
						{form.external_payment.error_message}
					</Message>
				)}
			</> : null)}
			<Message style={{ marginTop: 15 }} type='error' list={modalPrompts} />
		</Modal.Content>
		<Modal.Actions>
			<Button basic text='Cerrar' onClick={props.onClose} />
		</Modal.Actions>
	</Modal>
}

export default PaymentInfoModal;